import React, { useState, useEffect } from 'react';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaBoxOpen,
  FaLayerGroup,
  FaMoneyBillWave,
  FaShoppingBag
} from 'react-icons/fa';
import { productAPI, getImageUrl } from '../services/api';
import Modal from '../components/Modal';
import './ProductsPage.css';

function ProductsPage({
  readOnly = false,
  title = 'Product Inventory',
  subtitle = ''
}) {
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [purchaseProduct, setPurchaseProduct] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [formData, setFormData] = useState({
    ProductName: '',
    Category: '',
    Size: '',
    UnitPrice: '',
    StockQuantity: 0,
    ImagePath: ''
  });

  useEffect(() => {
    fetchProducts();
    if (readOnly) {
      fetchPurchases();
    }
  }, [readOnly]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAll();
      setProducts(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await productAPI.getMyPurchases();
      setPurchases(response.data || []);
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredProducts = products.filter((product) =>
    product.ProductName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingId(product.ProductID);
      setFormData(product);
    } else {
      setEditingId(null);
      setFormData({
        ProductName: '',
        Category: '',
        Size: '',
        UnitPrice: '',
        StockQuantity: 0,
        ImagePath: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file' && files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === 'ImagePath') {
          if (formData[key] instanceof File) {
            data.append(key, formData[key]);
          }
        } else {
          data.append(key, formData[key]);
        }
      });

      if (editingId) {
        await productAPI.update(editingId, data);
      } else {
        await productAPI.create(data);
      }
      await fetchProducts();
      handleCloseModal();
      setError('');
      setMessage('');
    } catch (err) {
      setError('Failed to save product: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productAPI.delete(id);
        fetchProducts();
        setError('');
        setMessage('');
      } catch (err) {
        setError('Failed to delete product: ' + err.message);
      }
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    setPurchasing(true);
    try {
      await productAPI.purchase({
        ProductID: purchaseProduct.ProductID,
        Quantity: purchaseQuantity
      });
      setMessage('Product purchased successfully.');
      setError('');
      setPurchaseProduct(null);
      setPurchaseQuantity(1);
      await fetchProducts();
      await fetchPurchases();
    } catch (err) {
      setError(err.response?.data?.error || ('Failed to purchase product: ' + err.message));
    } finally {
      setPurchasing(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') {
      return '0.00';
    }
    const numPrice = Number(price);
    if (isNaN(numPrice)) {
      return '0.00';
    }
    return numPrice.toFixed(2);
  };

  const formatStock = (stock) => {
    if (stock === null || stock === undefined) {
      return 0;
    }
    return Number(stock) || 0;
  };

  const getProductImage = (product) => {
    if (product?.ImagePath) {
      return (
        <img
          src={getImageUrl(product.ImagePath)}
          alt={product.ProductName}
          className="student-card-image"
        />
      );
    }

    return (
      <div className="student-card-image student-card-fallback">
        <FaShoppingBag />
      </div>
    );
  };

  return (
    <div className="products-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">{title}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
          {!readOnly && (
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <FaPlus /> Add Product
            </button>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {loading ? (
          <div className="loading">Loading products...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="no-data">No products found</div>
        ) : readOnly ? (
          <div className="student-card-grid">
            {filteredProducts.map((product) => (
              <article key={product.ProductID} className="student-detail-card">
                {getProductImage(product)}
                <div className="student-card-body">
                  <div className="student-card-chip-row">
                    <span className="student-card-chip">{product.Category || 'Skating Gear'}</span>
                    <span className="student-card-chip subtle">{`Rs ${formatPrice(product.UnitPrice)}`}</span>
                  </div>
                  <h3>{product.ProductName}</h3>
                  <div className="student-card-meta">
                    <span><FaLayerGroup /> Size: {product.Size || 'Standard'}</span>
                    <span><FaBoxOpen /> Stock: {formatStock(product.StockQuantity)}</span>
                    <span><FaMoneyBillWave /> Unit price: Rs {formatPrice(product.UnitPrice)}</span>
                  </div>
                  <button
                    className="btn btn-success student-card-action"
                    onClick={() => {
                      setPurchaseProduct(product);
                      setPurchaseQuantity(1);
                    }}
                    disabled={formatStock(product.StockQuantity) < 1}
                  >
                    {formatStock(product.StockQuantity) < 1 ? 'Out of Stock' : 'Buy Product'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Category</th>
                  <th>Size</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.ProductID}>
                    <td data-label="Product Name">{product.ProductName}</td>
                    <td data-label="Category">{product.Category || 'N/A'}</td>
                    <td data-label="Size">{product.Size || 'N/A'}</td>
                    <td data-label="Price">{`Rs ${formatPrice(product.UnitPrice)}`}</td>
                    <td data-label="Stock">{formatStock(product.StockQuantity)}</td>
                    <td data-label="Actions" className="action-buttons">
                      <button
                        className="btn btn-small btn-primary"
                        onClick={() => handleOpenModal(product)}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(product.ProductID)}
                      >
                        <FaTrash /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {readOnly && purchases.length > 0 && (
        <div className="container mt-20">
          <h2 className="page-title">My Product Purchases</h2>
          <div className="student-card-grid compact">
            {purchases.map((purchase) => (
              <article key={purchase.PurchaseID} className="student-detail-card purchase-card">
                <div className="student-card-body">
                  <div className="student-card-chip-row">
                    <span className="student-card-chip">{purchase.Category || 'Purchase'}</span>
                    <span className={`student-card-chip subtle status-${String(purchase.InvoiceStatus || 'Pending').toLowerCase()}`}>
                      {purchase.InvoiceStatus || 'Pending'}
                    </span>
                  </div>
                  <h3>{purchase.ProductName || `Product #${purchase.ProductID}`}</h3>
                  <div className="student-card-meta">
                    <span><FaBoxOpen /> Quantity: {purchase.Quantity}</span>
                    <span><FaMoneyBillWave /> Total: Rs {formatPrice(Number(purchase.Price) * Number(purchase.Quantity || 1))}</span>
                    <span><FaLayerGroup /> Date: {purchase.PurchaseDate ? String(purchase.PurchaseDate).split('T')[0] : 'N/A'}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {!readOnly && (
        <Modal
          isOpen={isModalOpen}
          title={editingId ? 'Edit Product' : 'Add New Product'}
          onClose={handleCloseModal}
        >
          <form onSubmit={handleSubmit} className="product-form">
            <div className="form-group">
              <label>Product Name *</label>
              <input
                type="text"
                name="ProductName"
                value={formData.ProductName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  name="Category"
                  value={formData.Category}
                  onChange={handleInputChange}
                  placeholder="e.g., Skates, Protective Gear"
                />
              </div>
              <div className="form-group">
                <label>Size</label>
                <input
                  type="text"
                  name="Size"
                  value={formData.Size}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Unit Price *</label>
                <input
                  type="number"
                  name="UnitPrice"
                  value={formData.UnitPrice}
                  onChange={handleInputChange}
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label>Stock Quantity</label>
                <input
                  type="number"
                  name="StockQuantity"
                  value={formData.StockQuantity}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Product Image</label>
              <input
                type="file"
                name="ImagePath"
                onChange={handleInputChange}
                accept="image/*"
              />
              <p className="form-help">Use a JPG or PNG image, recommended around 1200 x 900 px, so product cards look sharp on web and mobile.</p>
              {formData.ImagePath && (
                <div className="upload-preview">
                  <img
                    src={formData.ImagePath instanceof File ? URL.createObjectURL(formData.ImagePath) : getImageUrl(formData.ImagePath)}
                    alt="Product"
                  />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Product' : 'Add Product'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {readOnly && (
        <Modal
          isOpen={purchaseProduct !== null}
          title={purchaseProduct ? `Buy ${purchaseProduct.ProductName}` : 'Buy Product'}
          onClose={() => setPurchaseProduct(null)}
        >
          <form onSubmit={handlePurchase} className="product-form">
            <div className="form-group">
              <label>Product</label>
              <input type="text" value={purchaseProduct?.ProductName || ''} disabled />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Unit Price</label>
                <input type="text" value={purchaseProduct ? `Rs ${formatPrice(purchaseProduct.UnitPrice)}` : ''} disabled />
              </div>
              <div className="form-group">
                <label>Available Stock</label>
                <input type="text" value={purchaseProduct ? formatStock(purchaseProduct.StockQuantity) : 0} disabled />
              </div>
            </div>

            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                value={purchaseQuantity}
                onChange={(e) => setPurchaseQuantity(Number(e.target.value))}
                min="1"
                max={purchaseProduct ? formatStock(purchaseProduct.StockQuantity) : 1}
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={purchasing}>
                {purchasing ? 'Processing...' : 'Confirm Purchase'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setPurchaseProduct(null)} disabled={purchasing}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default ProductsPage;
